import React, { useEffect, useState } from "react";
import {
  Stack,
  Checkbox,
  Text,
  Card,
  HStack,
  IconButton,
  Spacer,
  Spinner,
  StackSeparator,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { t } from "i18next";
import { compressUUID } from "../../../../shared/utils/format-utils";
import { ApiClient, ResourceType } from "../../../models/ApiClient";
import { Project } from "../../../../project/models/Project";
import { AccountApi } from "../../../services/AccountApi";
import { OsaWay } from "../../../../osaway/models/OsaWay";
import {
  ArrowBackIcon,
  ArrowForwardIcon,
} from "../../../../shared/views/icons";

const i18prefix = "account:Integrations.permissions";

interface Props {
  apiClient: ApiClient;
  permissions: Map<string, ResourceType>;
  setPermissions: React.Dispatch<
    React.SetStateAction<Map<string, ResourceType>>
  >;
}

const ResourceSelectorBase: React.FC<Props> = ({
  apiClient,
  permissions,
  setPermissions,
}) => {
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<Project>();
  const [projects, setProjects] = useState<Project[]>();
  const [osaWays] = useState<OsaWay[]>();

  useEffect(() => {
    setLoading(true);
    AccountApi.listProjects(apiClient.accountId)
      .then(({ data: projects }) => {
        setProjects(projects);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // TODO uncomment and allow selection of campaigns and osaWays
    // if (selectedProject) {
    //   setLoading(true);
    //   ProjectApi.pageRoutes(selectedProject.id, undefined, [], 200, 0)
    //     .then(({ data: page }) => {
    //       setRoutes(page.data);
    //     })
    //     .finally(() => {
    //       setLoading(false);
    //     });
    // }
    // eslint-disable-next-line
  }, [selectedProject]);

  const onChangeItem =
    (resource: ResourceType, entity: string) => (checked: boolean) => {
      setPermissions((prev) => {
        if (checked) {
          prev.set(entity, resource);
        } else {
          prev.delete(entity);
        }
        return new Map(prev);
      });
    };

  return (
    <Card.Root>
      <Stack gap={0} separator={<StackSeparator />}>
        <HStack px={2} py={1}>
          <Text>
            {selectedProject
              ? t(`${i18prefix}.selectRoutes`, {
                  project: selectedProject.name,
                })
              : t(`${i18prefix}.selectProjects`)}
          </Text>
          <Spacer />
          {!!selectedProject && (
            <IconButton
              mx={-2}
              my={-1}
              colorPalette="primary"
              size="sm"
              variant="ghost"
              aria-label="Back to project"
              onClick={() => setSelectedProject(undefined)}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </HStack>

        <Stack
          gap={0}
          separator={<StackSeparator />}
          height="200px"
          overflowY="auto"
        >
          {loading ? (
            <HStack justifyContent="center" p={4}>
              <Spinner color="primary.600" />
            </HStack>
          ) : selectedProject ? (
            osaWays?.map((osaWay) => (
              <Item
                key={osaWay.id}
                id={osaWay.id}
                label={osaWay.name}
                isChecked={permissions.has(osaWay.id)}
                onChange={onChangeItem(ResourceType.OsaWay, osaWay.id)}
              />
            ))
          ) : (
            projects?.map((project) => (
              <Item
                key={project.id}
                id={project.id}
                label={`${project.city}: ${project.name}`}
                isChecked={permissions.has(project.id)}
                onChange={onChangeItem(ResourceType.Project, project.id)}
                // onExpand={() => {
                //   if (project.permissions?.canRead) {
                //     setSelectedProject(project);
                //   } else {
                //     window.alert(
                //       t(`${i18prefix}.noAccess`, { project: project.name })
                //     );
                //   }
                // }}
              />
            ))
          )}
        </Stack>
      </Stack>
    </Card.Root>
  );
};

export const ResourceSelector = observer(ResourceSelectorBase);

interface ItemProps {
  id: string;
  label: string;
  isChecked: boolean;
  onChange: (checked: boolean) => void;
  onExpand?: () => void;
}

const Item: React.FC<ItemProps> = ({
  id,
  label,
  isChecked,
  onChange,
  onExpand,
}) => {
  return (
    <HStack px={2} py={1}>
      <Checkbox.Root
        colorPalette="primary"
        size="sm"
        checked={isChecked}
        onCheckedChange={(e: any) => {
          const { checked } = e;
          onChange(checked);
        }}
      >
        <Checkbox.HiddenInput />
        <Checkbox.Control />
        <Checkbox.Label>{label}</Checkbox.Label>
      </Checkbox.Root>

      <Spacer />

      <Text fontFamily="monospace" fontSize="xs">
        {compressUUID(id)}
      </Text>

      {!!onExpand && (
        <IconButton
          mx={-2}
          my={-1}
          colorPalette="primary"
          size="sm"
          variant="ghost"
          aria-label="Select"
          onClick={onExpand}
        >
          <ArrowForwardIcon />
        </IconButton>
      )}
    </HStack>
  );
};
